import React from 'react';
import { IconButton, Menu, MenuItem, Tooltip, Box } from '@mui/material';
import LightModeOutlinedIcon from '@mui/icons-material/LightModeOutlined';
import DarkModeOutlinedIcon from '@mui/icons-material/DarkModeOutlined';
import SettingsBrightnessIcon from '@mui/icons-material/SettingsBrightness';
import CheckIcon from '@mui/icons-material/Check';
import { useThemeContext, ThemeMode } from '../../../contexts/ThemeContext';
import { useLanguage } from '../../../contexts/LanguageContext';

const modeIcons: Record<ThemeMode, React.ReactNode> = {
  light: <LightModeOutlinedIcon fontSize="small" />,
  dark: <DarkModeOutlinedIcon fontSize="small" />,
  system: <SettingsBrightnessIcon fontSize="small" />,
};

const modeKeys: Record<ThemeMode, string> = {
  light: 'theme.light',
  dark: 'theme.dark',
  system: 'theme.system',
};

const ThemeSelector: React.FC = () => {
  const { mode, setMode, isDark } = useThemeContext();
  const { t } = useLanguage();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const activeThemeIconMode: ThemeMode = mode === 'system' ? (isDark ? 'dark' : 'light') : mode;

  return (
    <>
      <Tooltip title={t('theme.name')}>
        <IconButton
          onClick={(e) => setAnchorEl(e.currentTarget)}
          size="small"
          sx={{ p: 0.5, color: 'text.secondary', '&:hover': { background: "none" } }}
        >
          {modeIcons[activeThemeIconMode]}
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: { minWidth: 150, mt: 0.5 } }}
        MenuListProps={{ sx: { py: 0 } }}
      >
        {(['light', 'dark', 'system'] as ThemeMode[]).map((m) => (
          <MenuItem
            key={m}
            onClick={() => { setMode(m); setAnchorEl(null); }}
            sx={{ gap: 1.5, fontSize: 12, py: 1 }}
          >
            <Box sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center' }}>
              {modeIcons[m]}
            </Box>
            {t(modeKeys[m])}
            {mode === m && (
              <CheckIcon fontSize="small" sx={{ ml: 'auto', color: 'primary.main' }} />
            )}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default ThemeSelector;
